import React from "react";
import { ActivityIndicator, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { RouteProp, useFocusEffect, useIsFocused, useNavigation } from "@react-navigation/native";
import { useSelector } from "react-redux";

import { tLinks, tRep } from "tmt_api/src/types/types";

import * as repDb from "src/db/rep.db";
import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { PdStack, PdText } from "src/components/layout";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdArrowUpRight, PdFacebookAlt, PdInstagram, PdLinkedin, PdMessage } from "src/components/Icons";
import { kRoute } from "src/kRoute";
import { setCurrentThread } from "src/redux/messages/actions";
import {
  PdProfileAvatar,
  PdProfileBanner,
  PdProfileCard,
  PdProfileSocialIconButton,
  PdProfileTag,
  PdProfileTagLabel,
} from "src/components/layout/display";
import { dRepCategories } from "tmt_api/src/shared/dics";

type tpViewrConnectionParamList = {
  rConnection: {
    id: string;
  };
};

const useIsLargeScreenSelector = () => {
  return useSelector((state) => state.NavigationReducer.deviceSize === "lg");
};

const Content: React.FC<{ data: tRep }> = ({ data }) => {
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const rIsLargeScreen = useIsLargeScreenSelector();
  const rMessagesThreads = useSelector((state) => state.MessageThreadsReducer.rThreads);

  const socialMedia: tLinks = data.links || data.socialMedia || {};

  const handleOnPressLink = (link?: string) => {
    window.open(link, "_blank");
  };

  const handleOnPressMessage = () => {
    try {
      const rThreadId = rMessagesThreads.find((thread) => thread.recipient.includes(data.id));
      if (!rThreadId) throw new Error("An error occurred while attempting to parse the recipient's id.");
      dispatch(setCurrentThread(rThreadId));
      navigation.navigate(kRoute.P04);
    } catch (er) {
      dispatch({
        type: "RIBBON",
        message: new Error("Please use the left menu to send a message to the Retailer"),
      });
    }
  };

  return (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.mb20}>
      <PdProfileBanner />
      <View style={rIsLargeScreen ? styles.containerWeb : styles.containerMobile}>
        <PdProfileAvatar source={{ uri: data.avatar }} />
        <View style={rIsLargeScreen ? styles.contentWeb : styles.contentMobile}>
          <View style={rIsLargeScreen ? styles.flex1 : {}}>
            <PdText style={styles.nameTitle}>{data.name}</PdText>
            <PdText style={styles.storeTitle}>{data.company ?? ""}</PdText>
            {data.address.city && data.address.state_or_province && (
              <PdText numberOfLines={1}>{`${data.address.city}, ${data.address.state_or_province}`}</PdText>
            )}
          </View>
          <View style={styles.messageButtonContainer}>
            <PdButton
              variant="outline"
              onPress={handleOnPressMessage}
              leftIcon={<PdMessage style={styles.messageIcon} />}
              style={rIsLargeScreen ? {} : styles.mt6}
            >
              <PdButtonLabel>Message</PdButtonLabel>
            </PdButton>
          </View>
        </View>
      </View>
      <PdStack
        style={rIsLargeScreen ? styles.cardsContainerWeb : styles.cardsContainerMobile}
        direction={rIsLargeScreen ? "horizontal" : "vertical"}
        gap={4}
        layout={[2, 1]}
      >
        <PdProfileCard>
          <PdText style={styles.cardTitle}>About Me</PdText>
          <PdText>{data.aboutMe || "-"}</PdText>
        </PdProfileCard>
        <PdProfileCard>
          {data.website && (
            <>
              <TouchableOpacity onPress={() => handleOnPressLink(data.website)} style={styles.profileButton}>
                <PdText>Website</PdText>
                <PdArrowUpRight style={styles.arrowUpIcon} color={kColors.brandDark} />
              </TouchableOpacity>
            </>
          )}
          <PdText style={styles.cardTitle}>Location</PdText>
          {data.address.city && data.address.state_or_province && (
            <PdText style={styles.mb3}>{`${data.address.city}, ${data.address.state_or_province}`}</PdText>
          )}
          <PdText style={styles.cardTitle}>Socials</PdText>
          {socialMedia.linkedin || socialMedia.instagram || socialMedia.facebook ? (
            <View style={styles.socialsContainer}>
              {socialMedia.linkedin && (
                <PdProfileSocialIconButton
                  style={styles.mr3}
                  onPress={() => handleOnPressLink(socialMedia.linkedin)}
                  icon={<PdLinkedin style={styles.sizeXs} />}
                />
              )}
              {socialMedia.instagram && (
                <PdProfileSocialIconButton
                  style={styles.mr3}
                  onPress={() => handleOnPressLink(socialMedia.instagram)}
                  icon={<PdInstagram style={styles.sizeXs} />}
                />
              )}
              {socialMedia.facebook && (
                <PdProfileSocialIconButton
                  onPress={() => handleOnPressLink(socialMedia.facebook)}
                  icon={<PdFacebookAlt style={styles.sizeXs} />}
                />
              )}
            </View>
          ) : (
            <PdText style={styles.mb3}>-</PdText>
          )}
          <PdText style={styles.cardTitle}>Sales Rep Categories</PdText>
          {(data.category?.length ?? 0) > 0 ? (
            <View style={styles.categoriesContainer}>
              {data.category?.map((category) => {
                const _category = dRepCategories[category];
                if (!_category) return null;
                return (
                  <PdProfileTag key={_category}>
                    <PdProfileTagLabel>{_category}</PdProfileTagLabel>
                  </PdProfileTag>
                );
              })}
            </View>
          ) : (
            <PdText>-</PdText>
          )}
        </PdProfileCard>
      </PdStack>
    </ScrollView>
  );
};

const P20_ViewSalesRep: React.FC<{ route: RouteProp<tpViewrConnectionParamList, "rConnection"> }> = ({ route }) => {
  const isFocused = useIsFocused();
  const [rep, setRep] = React.useState<tRep | undefined>(undefined);

  useFocusEffect(
    React.useCallback(() => {
      const unsubscribe = repDb.documentRep(route.params?.id).onSnapshot((document) => {
        setRep(document.data());
      });

      return () => {
        unsubscribe();
      };
    }, [route.params?.id]),
  );

  React.useEffect(() => {
    if (!isFocused) {
      setRep(undefined);
    }
  }, [isFocused]);

  if (!rep) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  return <Content data={rep} />;
};

export const P20 = React.memo(P20_ViewSalesRep);

const styles = StyleSheet.create({
  arrowUpIcon: {
    height: 12,
    marginLeft: kStyleConst.kSpacing,
    width: 12,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "400",
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  cardsContainerMobile: {
    alignItems: "flex-start",
    marginBottom: 6 * kStyleConst.kSpacing,
    marginHorizontal: 5 * kStyleConst.kSpacing,
  },
  cardsContainerWeb: {
    alignItems: "flex-start",
    marginBottom: 6 * kStyleConst.kSpacing,
    marginHorizontal: 20 * kStyleConst.kSpacing,
  },
  categoriesContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 2 * kStyleConst.kSpacing,
  },
  containerMobile: {
    marginBottom: 6 * kStyleConst.kSpacing,
    marginHorizontal: 5 * kStyleConst.kSpacing,
  },
  containerWeb: {
    flexDirection: "row",
    marginBottom: 6 * kStyleConst.kSpacing,
    marginHorizontal: 20 * kStyleConst.kSpacing,
  },
  contentMobile: {
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 3 * kStyleConst.kSpacing,
  },
  contentWeb: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    marginLeft: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  flex1: {
    flex: 1,
  },
  loadingContainer: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  mb20: {
    marginBottom: 20 * kStyleConst.kSpacing,
  },
  mb3: {
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  messageButtonContainer: {
    alignItems: "flex-end",
    flex: 1,
  },
  messageIcon: {
    height: 18,
    width: 18,
  },
  mr3: {
    marginRight: 3 * kStyleConst.kSpacing,
  },
  mt6: {
    marginTop: 6 * kStyleConst.kSpacing,
  },
  nameTitle: {
    fontSize: 22,
    fontWeight: "400",
    letterSpacing: 2,
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  profileButton: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  sizeXs: {
    height: 12,
    width: 12,
  },
  socialsContainer: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  storeTitle: {
    fontSize: 18,
    fontWeight: "400",
    letterSpacing: 2,
    marginBottom: 1 * kStyleConst.kSpacing,
  },
});
